import { MouseEventHandler, useEffect, useState } from "react";
import { pink, red, green, gold, purple } from "../config";
import { IconProps } from "@mui/material";

type ButtonVariant = "primary" | "secondary" | "tertiary" | "quaternary";
type ButtonPalette = "primary" | "neutral" | "green" | "red" | "yellow";
type ButtonSize = "small" | "medium" | "big";
interface StyledButtonProps {
  variant?: ButtonVariant;
  palette?: ButtonPalette;
  icon?: IconProps;
  iconPlacement?: "left" | "right";
  label?: string;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  href?: string;
  classes?: string;
  fullWidth?: boolean;
  size?: ButtonSize;
  disabled?: boolean;
  clickable?: boolean;
  style?: React.CSSProperties;
  children?: React.ReactNode;
  hasCooldown?: boolean;
}

const getBackground = (buttonVariant: ButtonVariant, buttonPalette: ButtonPalette) => {
  switch (buttonVariant) {
    case "primary":
      switch (buttonPalette) {
        case "primary":
          return `linear-gradient(92.3deg, ${pink[500]} 0.95%, ${pink[400]} 98.83%)`;
        case "green":
          return `linear-gradient(92.3deg, ${green[500]} 0.95%, ${green[400]} 98.83%)`;
        case "red":
          return `linear-gradient(92.3deg, ${red[500]} 0.95%, ${red[400]} 98.83%)`;
        case "yellow":
          return `linear-gradient(92.3deg, ${gold[500]} 0.95%, ${gold[300]} 98.83%)`;
        default:
          return "#fff";
      }
    case "secondary":
      switch (buttonPalette) {
        case "primary":
          return "#1db6eb";
        default:
          return "#fff";
      }
    case "tertiary":
      return "transparent";
    case "quaternary":
      return "transparent";
    default:
      return "#fff";
  }
};

const getTextAndBorderColor = (buttonVariant: ButtonVariant, buttonPalette: ButtonPalette) => {
  switch (buttonPalette) {
    case "primary":
      switch (buttonVariant) {
        case "primary":
          return "#fff";
        case "secondary":
          return "#013243";
        case "tertiary":
          return "#063955";
        case "quaternary":
          return purple[100];
        default:
          return "#000";
      }
    case "green":
      switch (buttonVariant) {
        case "primary":
          return "#053128";
        default:
          return "#000";
      }
    case "red":
      switch (buttonVariant) {
        case "primary":
          return "#FFE2E2";
        default:
          return "#000";
      }
    default:
      return "#000";
  }
};
const getPadding = (hasIcon: boolean, buttonSize: ButtonSize) => {
  switch (buttonSize) {
    case "small":
      return hasIcon ? "4px 14px" : "7px 14px";
    case "medium":
      return hasIcon ? "11px 22px" : "12px 22px";
    case "big":
      return hasIcon ? "9px 22px" : "14px 22px";
  }
};

const getHeight = (buttonSize: ButtonSize) => {
  switch (buttonSize) {
    case "small":
      return 36;
    case "medium":
      return 46;
    case "big":
      return 56;
  }
};

const StyledButton: React.FC<StyledButtonProps> = ({
  variant = "primary",
  palette = "primary",
  icon,
  onClick,
  label,
  iconPlacement = "left",
  href,
  classes = "",
  fullWidth = false,
  size = "medium",
  disabled = false,
  clickable = true,
  hasCooldown = false,
  style,
  children,
}: StyledButtonProps) => {
  const [bgd, setBgd] = useState(getBackground(variant, palette));
  const [textAndBorderColor, setTextAndBorderColor] = useState(
    getTextAndBorderColor(variant, palette)
  );
  const [padding, setPadding] = useState(getPadding(icon ? true : false, size));
  const [height, setHeight] = useState(getHeight(size));
  const [hover, setHover] = useState<boolean>();
  const [isDisabled, setIsDisabled] = useState<boolean>(disabled);

  useEffect(() => {
    setBgd(getBackground(variant, palette));
    setTextAndBorderColor(getTextAndBorderColor(variant, palette));
    setPadding(getPadding(icon ? true : false, size));
    setHeight(getHeight(size));
  }, [palette, variant, icon, size]);

  useEffect(() => {
    if (hasCooldown && disabled === false) {
      setIsDisabled(true);

      setTimeout(() => {
        setIsDisabled(false);
      }, 1000);
    }
  }, [hasCooldown, children, disabled]);

  return (
    <button
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={
        !isDisabled && clickable
          ? href
            ? () => window.location.replace(href)
            : onClick || undefined
          : undefined
      }
      style={{
        borderRadius: 100,
        cursor: isDisabled || clickable === false ? "not-allowed" : "pointer",
        fontSize: size === "small" ? 16 : size === "big" ? 20 : "inherit",
        fontWeight: "bold",
        fontFamily: "Dagny, Roboto, Helvetica, Arial, sans-serif",
        background: bgd,
        filter:
          isDisabled && clickable === true
            ? "brightness(0.8)"
            : hover && clickable
            ? "brightness(1.08)"
            : "none",
        transform: hover && !isDisabled && clickable ? "scale(1.03)" : "scale(1)",
        color: textAndBorderColor,
        boxShadow:
          variant === "tertiary"
            ? `0px 0px 0px 2px ${textAndBorderColor} inset`
            : variant === "quaternary"
            ? `0px 0px 0px 2px ${textAndBorderColor} inset`
            : "none",
        border: "none",
        padding: padding,
        minHeight: height,
        display: "inline-flex",
        lineHeight: 1.1,
        justifyContent: "center",
        alignItems: "center",
        transition: "all 0.2s ease",
        boxSizing: "border-box",
        textAlign: "center",
        width: fullWidth ? "100%" : "unset",
        textDecoration: "none",
        ...style,
      }}
      className={classes}
    >
      {icon && iconPlacement === "left" && (
        <div style={{ marginRight: size === "small" ? 6 : 12, marginBottom: -5 }}>
          {icon}
        </div>
      )}
      {label}
      {children}
      {icon && iconPlacement === "right" && (
        <div style={{ marginLeft: size === "small" ? 6 : 12, marginBottom: -5 }}>
          {icon}
        </div>
      )}
    </button>
  );
};

export default StyledButton;
